import { useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Edit, Calendar, Package, Tag, CheckCircle, XCircle, Users, Hash, Image as ImageIcon, Banknote, Percent, Store } from 'lucide-react'
import { getImageUrl } from '../../config/imageConfig'
import useOfferStore from '@/stores/offerStore'
import useAppConfigStore from '@/stores/appConfigStore'
import { offerService } from '@/services/offerService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const OfferDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentOffer,
    loading,
    error,
    fetchOfferById,
    updateOfferStatus
  } = useOfferStore()
  
  const { systemConfig, availableCurrencies } = useAppConfigStore()

  // Create a mapping of currency code to symbol
  const currencySymbolMap = useMemo(() => {
    const map: Record<string, string> = {}
    availableCurrencies.forEach((currency) => {
      map[currency.code] = currency.symbol || currency.code
    })
    return map
  }, [availableCurrencies])

  useEffect(() => {
    if (id) {
      fetchOfferById(id)
    }
  }, [id])

  if (loading && !currentOffer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'offre...</p>
        </div>
      </div>
    )
  }

  if (error || !currentOffer) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Offre non trouvée"
          breadcrumbs={[
            { label: 'Offres', href: '/offers' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/offers"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Tag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Offre non trouvée</h3>
              <p className="text-gray-500 mb-4">
                {error || "L'offre que vous recherchez n'existe pas."}
              </p>
              <Button onClick={() => navigate('/offers')}>
                Retour à la liste
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleToggleStatus = async () => {
    await updateOfferStatus(currentOffer.offer_id, !currentOffer.is_active)
  }

  const getTypeColor = (type: string) => {
    const typeConfig = offerService.getOfferTypes().find(t => t.value === type)
    return typeConfig?.color || 'bg-gray-100 text-gray-800'
  }

  const getTypeLabel = (type: string) => {
    const typeConfig = offerService.getOfferTypes().find(t => t.value === type)
    return typeConfig?.label || type
  }

  const formatPrice = (price: any, currencyCode?: string) => {
    if (price === null || price === undefined) return 'N/D'
    const num = typeof price === 'string' ? parseFloat(price) : Number(price)
    if (isNaN(num) || !Number.isFinite(num)) return 'N/D'
    
    // Use provided currency code, otherwise use offer's currency_code, otherwise system default
    const effectiveCurrencyCode = currencyCode ||
                                 currentOffer?.currency_code ||
                                 systemConfig?.default_currency ||
                                 ''
    const symbol = currencySymbolMap[effectiveCurrencyCode] || effectiveCurrencyCode
    // If symbol is a single character (like €, $, £) place before price, otherwise after
    if (symbol.length === 1 || symbol.length === 2) {
      return `${symbol}${num.toFixed(2)}`
    } else {
      return `${num.toFixed(2)} ${symbol}`
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Non définie'
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const discountPercentage = offerService.calculateDiscountPercentage(
    currentOffer.original_price,
    currentOffer.discount_price
  )

  const offer = currentOffer

  const breadcrumbs = [
    { label: 'Offres', href: '/offers' },
    { label: offer.title, href: '#' }
  ]

  const actions = [
    {
      label: offer.is_active ? 'Désactiver' : 'Activer',
      icon: offer.is_active ? <XCircle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />,
      onClick: handleToggleStatus,
      variant: offer.is_active ? 'destructive' as const : 'default' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-5 w-5" />,
      onClick: () => navigate(`/offers/${offer.offer_id}/edit`),
      variant: 'default' as const
    }
  ]

  const status = offer.is_active 
    ? { label: 'Actif', variant: 'success' as const }
    : { label: 'Inactif', variant: 'destructive' as const }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={offer.title}
        subtitle={`ID: ${offer.offer_id}`}
        breadcrumbs={breadcrumbs}
        backUrl="/offers"
        actions={actions}
        status={status}
      />

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Offer details */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Description">
            <p className="text-gray-700 whitespace-pre-line">
              {offer.description || 'Aucune description fournie.'}
            </p>
          </DetailSection>

          <DetailSection title="Images">
            {offer.images && offer.images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {offer.images.map((image, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={getImageUrl(image.image_url)}
                      alt={`${offer.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center">
                              <ImageIcon class="h-8 w-8 text-gray-400" />
                            </div>
                          `;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucune image disponible</p>
              </div>
            )}
          </DetailSection>

          <DetailSection title="Prix">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Prix original</div>
                <div className="flex items-center text-lg font-medium">
                  <Banknote className="h-5 w-5 mr-2 text-gray-400" />
                  {formatPrice(offer.original_price)}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Prix réduit</div>
                <div className="flex items-center text-lg font-medium text-green-600">
                  <Banknote className="h-5 w-5 mr-2 text-green-400" />
                  {formatPrice(offer.discount_price)}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Économie</div>
                <div className="text-lg font-medium text-red-600">
                  <Percent className="h-5 w-5 mr-2 inline text-red-400" />
                  {discountPercentage > 0 ? `-${discountPercentage}%` : 'N/D'}
                  {offer.discount_percentage && (
                    <span className="text-sm text-gray-500 ml-2">
                      ({offer.discount_percentage}% configuré)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </DetailSection>

          {/* Products */}
          {offer.products && offer.products.length > 0 && (
            <DetailSection title={`Produits inclus (${offer.products.length})`}>
              <div className="space-y-4">
                {offer.products.map((product, index) => (
                  <div key={product.product_id || index} className="flex items-start gap-4 p-4 border border-gray-100 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="text-sm font-medium text-gray-900">
                          {formatPrice(product.price, offer.currency_code || undefined)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Quantité: <span className="font-medium">{product.quantity}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          <DetailSection title="Dates de validité">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  Date de début
                </div>
                <div className="font-medium">{formatDate(offer.start_date)}</div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  Date de fin
                </div>
                <div className="font-medium">{formatDate(offer.end_date)}</div>
              </div>
            </div>
          </DetailSection>
        </div>

        {/* Right column - Metadata */}
        <div className="space-y-6">
          <DetailSection title="Type et statut">
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Type d'offre</div>
                <Badge variant="outline" className={getTypeColor(offer.type)}>
                  <Tag className="h-4 w-4 mr-2" />
                  {getTypeLabel(offer.type)}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Statut</div>
                {offer.is_active ? (
                  <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="h-4 w-4 mr-1" />
                    Inactive
                  </Badge>
                )}
              </div>
              {offer.product_count !== undefined && (
                <div>
                  <div className="text-sm text-gray-600 mb-1">Nombre de produits</div>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    <Package className="h-4 w-4 mr-2" />
                    {offer.product_count} produit(s)
                  </Badge>
                </div>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Marchand">
            <div className="space-y-3">
              {offer.merchant_name ? (
                <div>
                  <div className="text-sm text-gray-600">Nom du marchand</div>
                  <div className="font-medium">{offer.merchant_name}</div>
                </div>
              ) : (
                <div>
                  <div className="text-sm text-gray-600">ID Marchand</div>
                  <div className="flex items-center font-medium">
                    <Hash className="h-4 w-4 mr-2 text-gray-400" />
                    {offer.merchant_id}
                  </div>
                </div>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Catégorie">
            <div className="space-y-3">
              {offer.category_id ? (
                <>
                  {offer.category_name ? (
                    <div>
                      <div className="text-sm text-gray-600">Nom de la catégorie</div>
                      <div className="font-medium">{offer.category_name}</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm text-gray-600">ID Catégorie</div>
                      <div className="flex items-center font-medium">
                        <Package className="h-4 w-4 mr-2 text-gray-400" />
                        {offer.category_id}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-500">Aucune catégorie associée</p>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Quantité">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-600">Quantité maximale</div>
                <div className="flex items-center font-medium">
                  <Users className="h-4 w-4 mr-2 text-gray-400" />
                  {offer.max_quantity || 'Illimité'}
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Dates de création">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-600">Créée le</div>
                <div className="font-medium">{formatDate(offer.created_at)}</div>
              </div>
              {offer.updated_at && (
                <div>
                  <div className="text-sm text-gray-600">Modifiée le</div>
                  <div className="font-medium">{formatDate(offer.updated_at)}</div>
                </div>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/offers/${id}/edit`)}
              >
                <Edit className="h-5 w-5 mr-2" />
                Modifier l'offre
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/products?offerId=${offer.offer_id}`)}
              >
                <Package className="h-5 w-5 mr-2" />
                Voir les produits
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/merchants/${offer.merchant_id}`)}
              >
                <Store className="h-5 w-5 mr-2" />
                Voir le marchand
              </Button>
              
              <Button
                variant={offer.is_active ? "destructive" : "default"}
                className="w-full justify-start"
                onClick={handleToggleStatus}
              >
                {offer.is_active ? (
                  <>
                    <XCircle className="h-5 w-5 mr-2" />
                    Désactiver l'offre
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Activer l'offre
                  </>
                )}
              </Button>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default OfferDetails
